import { createSelector } from '@reduxjs/toolkit';
import { selectPersonalClockedInWorkRecords } from 'store/records/selectors/records.selectors';
import {
  getUserProjectPreferenceKey,
  isProjectHiddenByPreferenceOrAncestor,
} from '../utils/userProjectPreferences';
import {
  LATEST_PROJECTS_SOURCE_MODES,
  selectLatestUserActiveProjects,
  selectLatestUserRecordProjectIds,
  selectProjects,
  selectProjectsArrayWithJoinedData,
  selectTimerDrawerSourceMode,
  selectUserProjectPreferences,
} from './projectsSelectors';

export const selectPersonalClockedInRecordsProjectIds = createSelector(
  [selectPersonalClockedInWorkRecords],
  (records) => records.map((record) => record.project.id)
);

// Flat list of the projects the user has own records in during the last 30
// days — strictly the recorded projects (no ancestor expansion, unlike the
// Projects-list chain in projectsSelectors), minus hidden ones.
export const selectLatestRecordProjectsForDrawer = createSelector(
  [
    selectProjectsArrayWithJoinedData,
    selectLatestUserRecordProjectIds,
    selectUserProjectPreferences,
    selectProjects,
  ],
  (projects, recordProjectIds, preferences, projectsById) =>
    projects.filter(
      (project) =>
        recordProjectIds.includes(project.id) &&
        !isProjectHiddenByPreferenceOrAncestor(
          project,
          preferences,
          projectsById
        )
    )
);

// Drives the navbar Timer drawer's "Latest projects" list, split into two
// groups: { clockedIn, rest }. The source is picked by the drawer's toggle:
// RECORDS (default) lists only projects the user has own records in during the
// last 30 days, ANY_ACTIVITY the full 30-day activity list. Clocked-in
// projects (rendered as ProjectListItems with the edit-record button) go to
// the TOP, separated from the rest by a divider — mirroring how the source
// list divides the employer project from the rest. Any currently clocked-in
// project missing from the source list (e.g. older than 30 days, or an
// employer project) is force-included so active work is always visible.
// Within each group, favorited projects are hoisted first (preserving the
// existing order otherwise).
export const selectLatestActiveProjectsForTimerDrawer = createSelector(
  [
    selectTimerDrawerSourceMode,
    selectLatestUserActiveProjects,
    selectLatestRecordProjectsForDrawer,
    selectProjectsArrayWithJoinedData,
    selectPersonalClockedInRecordsProjectIds,
    selectUserProjectPreferences,
  ],
  (
    sourceMode,
    activeProjects,
    recordProjects,
    allProjects,
    personalClockedInProjectIds,
    preferences
  ) => {
    const sourceProjects =
      sourceMode === LATEST_PROJECTS_SOURCE_MODES.RECORDS
        ? recordProjects
        : activeProjects;
    const clockedInIdSet = new Set(personalClockedInProjectIds);

    const activeFiltered = sourceProjects.filter(
      (project) =>
        project.businessTypeEnum !== 'EMPLOYER' &&
        project.status !== 'ARCHIVED'
    );
    const activeIdSet = new Set(activeFiltered.map((project) => project.id));

    const clockedInMissing = allProjects.filter(
      (project) =>
        clockedInIdSet.has(project.id) && !activeIdSet.has(project.id)
    );

    // Stable hoist of favorited projects to the front of a group.
    const favoritesFirst = (projects) => {
      const favorites = [];
      const others = [];
      projects.forEach((project) => {
        const isFavorite = Boolean(
          preferences[getUserProjectPreferenceKey(project)]?.favorite
        );
        (isFavorite ? favorites : others).push(project);
      });
      return [...favorites, ...others];
    };

    return {
      clockedIn: favoritesFirst([
        ...clockedInMissing,
        ...activeFiltered.filter((project) => clockedInIdSet.has(project.id)),
      ]),
      rest: favoritesFirst(
        activeFiltered.filter((project) => !clockedInIdSet.has(project.id))
      ),
    };
  }
);
